/**
 * SessionConfig — the duration + mode picker for a single category.
 *
 * Route: `/quiet-hours/:category`
 *
 * Layout: the category's atmosphere field is gently in the background.
 * The center is a calm card with:
 *  - the category title (display type, slow fade-in)
 *  - the long description (plain English)
 *  - duration picker (four pills: 1 / 3 / 5 / 10 min)
 *  - mode picker (two pills: Guided / Just the room)
 *  - Begin button
 *
 * No haptic, no SFX on selection. Selecting a duration just shifts a
 * soft outline.
 */
import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  AtmosphereSurface,
  buildSrcSet,
  variantOf,
} from '@/juice/RevealedImage';
import { usePreferences } from '@/preferences';

import {
  DURATIONS_MIN,
  getCategory,
  type DurationMinutes,
  type QuietHoursCategoryMeta,
} from './categories';
import { FADE } from './motion';
import { voiceFileExists } from './voice';

import './SessionConfig.css';

export function QuietHoursSessionConfig() {
  const { category: slug } = useParams<{ category: string }>();
  const { theme } = usePreferences();
  const navigate = useNavigate();

  const category = useMemo(
    () => (slug ? getCategory(slug) : undefined),
    [slug],
  );

  const [duration, setDuration] = useState<DurationMinutes>(5);
  const [guided, setGuided] = useState(true);

  // Probe whether any voice files exist for this category. If none do,
  // gray out the guided option and default to unguided. The probe is
  // cheap (one HEAD per duration) and only runs when the user lands on
  // this screen.
  const [guidedAvailable, setGuidedAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    void (async () => {
      const probes = await Promise.all(
        DURATIONS_MIN.map((d) => voiceFileExists(category.slug, d)),
      );
      if (cancelled) return;
      const anyAvailable = probes.some((ok) => ok);
      setGuidedAvailable(anyAvailable);
      if (!anyAvailable) setGuided(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [category]);

  if (!category) {
    return (
      <div className="qh-config qh-config--missing" data-theme={theme}>
        <p>That space isn&apos;t available yet.</p>
      </div>
    );
  }

  const bg = theme === 'dark' ? category.atmosphereDark : category.atmosphereLight;

  const begin = (): void => {
    const url = `/quiet-hours/${category.slug}/${duration}?guided=${guided ? '1' : '0'}`;
    navigate(url);
  };

  return (
    <div className="qh-config" data-theme={theme}>
      <div className="qh-config__field" aria-hidden="true">
        <AtmosphereSurface
          src={bg}
          avifSrc={variantOf(bg, 'avif')}
          webpSrc={variantOf(bg, 'webp')}
          avifSrcSet={buildSrcSet(bg, 'avif', [430, 860, 1366])}
          webpSrcSet={buildSrcSet(bg, 'webp', [430, 860, 1366])}
          sizes="100vw"
          eager
        />
      </div>
      <div className="qh-config__scrim" aria-hidden="true" />

      <motion.section
        className="qh-config__card"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={FADE.in}
        aria-labelledby="qh-config-title"
      >
        <h1 id="qh-config-title" className="qh-config__title">
          {category.title}
        </h1>
        <p className="qh-config__description">{category.longDescription}</p>

        <DurationPicker
          value={duration}
          onChange={setDuration}
          category={category}
        />

        <ModePicker
          guided={guided}
          onChange={setGuided}
          available={guidedAvailable}
        />

        <button
          type="button"
          className="qh-config__begin"
          onClick={begin}
        >
          Begin
        </button>
      </motion.section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DurationPicker
// ---------------------------------------------------------------------------

function DurationPicker({
  value,
  onChange,
  category,
}: {
  value: DurationMinutes;
  onChange: (v: DurationMinutes) => void;
  category: QuietHoursCategoryMeta;
}) {
  void category; // available for future per-category tuning
  return (
    <fieldset className="qh-config__group">
      <legend className="qh-config__legend">How long?</legend>
      <div className="qh-config__pills" role="radiogroup" aria-label="Session length">
        {DURATIONS_MIN.map((d) => (
          <button
            key={d}
            type="button"
            role="radio"
            aria-checked={value === d}
            className={`qh-config__pill${value === d ? ' is-on' : ''}`}
            onClick={() => onChange(d)}
          >
            <span className="qh-config__pill-num">{d}</span>
            <span className="qh-config__pill-unit">{d === 1 ? 'minute' : 'minutes'}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// ModePicker
// ---------------------------------------------------------------------------

function ModePicker({
  guided,
  onChange,
  available,
}: {
  guided: boolean;
  onChange: (g: boolean) => void;
  available: boolean | null;
}) {
  const guidedDisabled = available === false;
  return (
    <fieldset className="qh-config__group">
      <legend className="qh-config__legend">With or without a voice?</legend>
      <div className="qh-config__pills qh-config__pills--mode" role="radiogroup" aria-label="Session mode">
        <button
          type="button"
          role="radio"
          aria-checked={guided}
          aria-disabled={guidedDisabled || undefined}
          disabled={guidedDisabled}
          className={`qh-config__pill qh-config__pill--mode${guided ? ' is-on' : ''}`}
          onClick={() => !guidedDisabled && onChange(true)}
        >
          <span className="qh-config__pill-mode-title">Guided</span>
          <span className="qh-config__pill-mode-desc">A warm voice talks you through it.</span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={!guided}
          className={`qh-config__pill qh-config__pill--mode${!guided ? ' is-on' : ''}`}
          onClick={() => onChange(false)}
        >
          <span className="qh-config__pill-mode-title">Just the room</span>
          <span className="qh-config__pill-mode-desc">Quiet ambient sound. No voice.</span>
        </button>
      </div>
      {guidedDisabled && (
        <p className="qh-config__note">
          A voice isn&apos;t available for this space right now. The room is still here for you.
        </p>
      )}
    </fieldset>
  );
}
